import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Loader2, Target, BarChart3, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIFInteligencia } from '../hooks/useIFInteligencia';
import { IFRiskTrendChart } from '../components/IFRiskTrendChart';
import { IFRiskHeatmap } from '../components/IFRiskHeatmap';
import { scoreColor, trendIcon, riskDistColor } from '../components/IFRiskBadges';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import { DataTable, type Column } from '@/components/system/DataTable';
import { EmptyState } from '@/components/system/EmptyState';
import type { ProjecaoRisco } from '../types/financiamento.types';

const IFRiscoPage: React.FC = () => {
  const navigate = useNavigate();
  const { resumos, isLoading, projecoes, alertasConcentracao } = useIFInteligencia();
  const [projExpanded, setProjExpanded] = useState(false);

  const riskDist = useMemo(() => {
    const d = { critico: 0, alto: 0, moderado: 0, baixo: 0 };
    resumos.forEach(r => {
      const c = r.classificacao_risco || '';
      if (c === 'crítico' || c === 'critico') d.critico++;
      else if (c === 'alto') d.alto++;
      else if (c === 'moderado') d.moderado++;
      else d.baixo++;
    });
    return d;
  }, [resumos]);

  const projecoesVisiveis = projExpanded ? projecoes : projecoes.slice(0, 5);

  const projColumns: Column<ProjecaoRisco>[] = [
    { key: 'contrato', label: 'Contrato', render: r => <span className="font-medium max-w-[200px] truncate block">{r.contrato_nome}</span> },
    { key: 'atual', label: 'Atual', align: 'center', render: r => <span className={`font-medium ${scoreColor(r.score_atual)}`}>{r.score_atual.toFixed(1)}</span> },
    { key: '30d', label: '30d', align: 'center', render: r => <span className={scoreColor(r.proj_30d)}>{r.proj_30d.toFixed(1)}</span> },
    { key: '60d', label: '60d', align: 'center', render: r => <span className={scoreColor(r.proj_60d)}>{r.proj_60d.toFixed(1)}</span> },
    { key: '90d', label: '90d', align: 'center', render: r => <span className={scoreColor(r.proj_90d)}>{r.proj_90d.toFixed(1)}</span> },
    { key: 'dias', label: 'Dias Rest.', align: 'center', render: r => <span className="text-sm">{r.dias_restantes}d</span> },
    { key: 'tend', label: 'Tendência', align: 'center', render: r => trendIcon(r.tendencia) },
  ];

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
        <PageHeader
          title="Inteligência de Risco"
          subtitle="Projeções, tendências e análise de risco operacional dos contratos monitorados"
        />

        <Tabs defaultValue="projecoes" className="space-y-4">
          <TabsList>
            <TabsTrigger value="projecoes">Projeções</TabsTrigger>
            <TabsTrigger value="mapa">Mapa de Risco</TabsTrigger>
          </TabsList>

          <TabsContent value="projecoes" className="space-y-4">
            <SectionCard title="Projeção de Risco — 30 / 60 / 90 dias" icon={Target}>
              <p className="text-xs text-muted-foreground mb-3">Estimativa baseada em tendência histórica e progresso físico</p>
              <DataTable
                columns={projColumns}
                data={projecoesVisiveis}
                keyExtractor={r => r.contrato_id}
                emptyText="Nenhum contrato monitorado ativo."
                onRowClick={r => navigate(`/financiamento/contrato/${r.contrato_id}`)}
              />
              {projecoes.length > 5 && (
                <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setProjExpanded(!projExpanded)}>
                  {projExpanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                  {projExpanded ? 'Mostrar menos' : `Ver todos (${projecoes.length})`}
                </Button>
              )}
            </SectionCard>
          </TabsContent>

          <TabsContent value="mapa" className="space-y-4">
            <SectionCard title="Distribuição de Risco" icon={BarChart3}>
              <div className="space-y-3">
                {[
                  { label: 'Crítico', key: 'critico', count: riskDist.critico },
                  { label: 'Alto', key: 'alto', count: riskDist.alto },
                  { label: 'Moderado', key: 'moderado', count: riskDist.moderado },
                  { label: 'Baixo', key: 'baixo', count: riskDist.baixo },
                ].map(r => (
                  <div key={r.label} className="flex items-center gap-3">
                    <span className={`w-3 h-3 rounded-full ${riskDistColor(r.key)} shrink-0`} />
                    <span className="text-sm flex-1">{r.label}</span>
                    <span className="text-sm font-medium">{r.count}</span>
                    <Progress value={resumos.length > 0 ? (r.count / resumos.length) * 100 : 0} className="h-1.5 w-24" />
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Heatmap — Contratada × Risco" icon={BarChart3}>
              <p className="text-xs text-muted-foreground mb-3">Distribuição de risco por contratada com tooltips de contratos específicos</p>
              <IFRiskHeatmap resumos={resumos} />
            </SectionCard>

            <SectionCard title="Contratos com Risco Crescente" icon={BarChart3}>
              <p className="text-xs text-muted-foreground mb-3">Projeção de score crescente nos próximos 90 dias</p>
              {projecoes.filter(p => p.risco_crescente).length === 0 ? (
                <EmptyState text="Nenhum contrato com tendência de risco crescente." icon={Target} />
              ) : (
                <div className="space-y-3">
                  {projecoes.filter(p => p.risco_crescente).map(p => (
                    <div
                      key={p.contrato_id}
                      className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/financiamento/contrato/${p.contrato_id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.contrato_nome}</p>
                        <p className="text-xs text-muted-foreground">{p.dias_restantes} dias restantes</p>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className={scoreColor(p.score_atual)}>{p.score_atual.toFixed(0)}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className={scoreColor(p.proj_90d)}>{p.proj_90d.toFixed(0)}</span>
                      </div>
                      <Badge variant="destructive" className="text-xs">+{(p.proj_90d - p.score_atual).toFixed(0)}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </TabsContent>
        </Tabs>
    </div>
  );
};

export default IFRiscoPage;
